import React from 'react'
import { flexRender, Table, VisibilityState } from '@tanstack/react-table'
import { useTypedSelector } from 'helpers/reducers'
import { SavedTableView } from 'types/entities/SavedTableView'
import { getActiveTableViewStorageKey } from 'utils/table/activeTableView'
import { toNumericViewId } from 'utils/table/tableViewId'
import {useTranslation} from 'react-i18next'
import {TableColumnMeta} from 'utils/table/tableColumnWidths'
import {getTableColumnLabel} from 'utils/table/tableColumnLabels'

const extractTextFromNode = (node: React.ReactNode): string | null => {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node).trim() || null
    }

    if (Array.isArray(node)) {
        for (const child of node) {
            const text = extractTextFromNode(child)
            if (text) {
                return text
            }
        }
        return null
    }

    if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
        return extractTextFromNode(node.props.children)
    }

    return null
}

export const useTableColumnVisibilityDraft = <TData,>(
    table: Table<TData>,
    enabled: boolean
) => {
    const {t} = useTranslation()
    const scrollContainerRef = React.useRef<HTMLDivElement>(null)
    const [draftVisibility, setDraftVisibility] = React.useState<VisibilityState>({})
    const [draftOrder, setDraftOrder] = React.useState<string[]>([])
    const [activeViewId, setActiveViewId] = React.useState<number | null>(null)
    const tableViews = useTypedSelector((state) => state.tableViewsState.views)

    const tableId = React.useMemo(() => {
        const meta = table.options.meta as { tableId?: string } | undefined
        return meta?.tableId ?? null
    }, [table])

    const allowedColumns = React.useMemo(
        () =>
            table
                .getAllLeafColumns()
                .filter((column) => column.id !== 'actions' && column.id !== 'select'),
        [table]
    )

    const viewsForTable = React.useMemo(
        () => tableViews.filter((view: SavedTableView) => view.table_id === tableId),
        [tableViews, tableId]
    )

    React.useEffect(() => {
        if (!enabled || !tableId) {
            return
        }

        try {
            const storedId = window.sessionStorage.getItem(getActiveTableViewStorageKey(tableId))
            if (!storedId) {
                setActiveViewId(null)
                return
            }

            const numericId = toNumericViewId(storedId)
            setActiveViewId(numericId)
        } catch {
            setActiveViewId(null)
        }
    }, [enabled, tableId])

    const activeViewName = React.useMemo(() => {
        if (activeViewId === null) {
            return null
        }

        return viewsForTable.find((view) => toNumericViewId(view.id) === activeViewId)?.name ?? null
    }, [activeViewId, viewsForTable])

    const headerByColumnId = React.useMemo(() => {
        const headers = table
            .getFlatHeaders()
            .filter(
                (header) =>
                    !header.isPlaceholder &&
                    header.column.id !== 'actions' &&
                    header.column.id !== 'select'
            )

        return new Map(headers.map((header) => [header.column.id, header]))
    }, [table])

    const getColumnLabel = React.useCallback(
        (column: (typeof allowedColumns)[number]): string => {
            const header = headerByColumnId.get(column.id)
            if (header) {
                const renderedHeader = flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                )
                const textLabel = extractTextFromNode(renderedHeader)
                if (textLabel) {
                    return textLabel
                }
            }

            if (typeof column.columnDef.header === 'string') {
                return column.columnDef.header
            }

            if (React.isValidElement<{ children?: React.ReactNode }>(column.columnDef.header)) {
                const textLabel = extractTextFromNode(column.columnDef.header.props.children)
                if (textLabel) {
                    return textLabel
                }
            }

            // Function headers are React elements whose text is not available until render.
            // Reuse the column's explicit label key instead of exposing its raw API id.
            const labelKey = (column.columnDef.meta as TableColumnMeta | undefined)?.exportFields?.[0]?.labelKey
            return labelKey ? t(labelKey) : getTableColumnLabel(column.id, t, tableId ?? undefined)
        },
        [headerByColumnId, t, tableId]
    )

    const initializeDraftFromTable = React.useCallback(() => {
        const state = table.getState()
        const stateOrder = state.columnOrder ?? []
        const allowedIds = allowedColumns.map((column) => column.id)
        const keptStateOrder = stateOrder.filter((id) => allowedIds.includes(id))
        const appendedIds = allowedIds.filter((id) => !keptStateOrder.includes(id))

        setDraftVisibility(state.columnVisibility ?? {})
        setDraftOrder([...keptStateOrder, ...appendedIds])
    }, [table, allowedColumns])

    React.useEffect(() => {
        if (!enabled) {
            return
        }

        initializeDraftFromTable()
    }, [enabled, initializeDraftFromTable])

    const toggleDraftVisibility = React.useCallback(
        (columnId: string) => {
            const column = table.getColumn(columnId)
            if (!column) {
                return
            }

            setDraftVisibility((previous) => {
                const currentVisible =
                    typeof previous[columnId] === 'boolean'
                        ? previous[columnId]
                        : column.getIsVisible()
                return {
                    ...previous,
                    [columnId]: !currentVisible,
                }
            })
        },
        [table]
    )

    const isColumnVisibleInDraft = React.useCallback(
        (column: (typeof allowedColumns)[number]) => {
            return typeof draftVisibility[column.id] === 'boolean'
                ? draftVisibility[column.id]
                : column.getIsVisible()
        },
        [draftVisibility]
    )

    const visibleColumnCount = React.useMemo(
        () => allowedColumns.filter((column) => isColumnVisibleInDraft(column)).length,
        [allowedColumns, isColumnVisibleInDraft]
    )

    const allColumnsVisible =
        allowedColumns.length > 0 && visibleColumnCount === allowedColumns.length
    const someColumnsVisible = visibleColumnCount > 0

    const checkAllDraft = React.useCallback(() => {
        setDraftVisibility((previous) => {
            const nextVisibility = { ...previous }
            allowedColumns.forEach((column) => {
                nextVisibility[column.id] = true
            })
            return nextVisibility
        })
    }, [allowedColumns])

    const uncheckAllDraft = React.useCallback(() => {
        setDraftVisibility((previous) => {
            const nextVisibility = { ...previous }
            allowedColumns.forEach((column) => {
                nextVisibility[column.id] = false
            })
            return nextVisibility
        })
    }, [allowedColumns])

    const hasDraftChanges = React.useMemo(() => {
        const state = table.getState()
        const currentVisibility = state.columnVisibility ?? {}
        const currentOrder = state.columnOrder ?? []
        const allowedIds = allowedColumns.map((column) => column.id)

        for (const column of allowedColumns) {
            const draftVisible = isColumnVisibleInDraft(column)
            const currentVisible =
                typeof currentVisibility[column.id] === 'boolean'
                    ? currentVisibility[column.id]
                    : column.getIsVisible()

            if (draftVisible !== currentVisible) {
                return true
            }
        }

        const normalizedCurrentOrder = [
            ...currentOrder.filter((id) => allowedIds.includes(id)),
            ...allowedIds.filter((id) => !currentOrder.includes(id)),
        ]

        return draftOrder.join('|') !== normalizedCurrentOrder.join('|')
    }, [allowedColumns, draftOrder, isColumnVisibleInDraft, table])

    const applyDraft = React.useCallback(() => {
        table.setColumnVisibility(draftVisibility)
        table.setColumnOrder(draftOrder)
    }, [table, draftVisibility, draftOrder])

    const resetDraft = React.useCallback(() => {
        const allowedIds = allowedColumns.map((column) => column.id)
        const defaultVisibility = allowedIds.reduce<VisibilityState>((visibility, columnId) => {
            visibility[columnId] = true
            return visibility
        }, {})

        setDraftVisibility(defaultVisibility)
        setDraftOrder(allowedIds)
        table.setColumnVisibility(defaultVisibility)
        table.setColumnOrder(allowedIds)
    }, [table, allowedColumns])

    return {
        allowedColumns,
        draftVisibility,
        draftOrder,
        getColumnLabel,
        toggleDraftVisibility,
        checkAllDraft,
        uncheckAllDraft,
        allColumnsVisible,
        someColumnsVisible,
        hasDraftChanges,
        applyDraft,
        resetDraft,
        scrollContainerRef,
        activeViewName,
        setDraftOrder,
    }
}
